/**
 * Utility functions for variant option handling
 * Shared between color selector, needle selector, and other variant components
 */

/**
 * Cleans a product title by removing specified words
 * @param {string} title - The title to clean
 * @param {Array<string>} wordsToRemove - Array of words to remove from the title
 * @returns {string} - The cleaned title
 */
function cleanProductTitle(title, wordsToRemove = []) {
  if (!title || typeof title !== 'string') {
    return '';
  }

  let cleanedTitle = title;

  wordsToRemove.forEach((word) => {
    const regex = new RegExp(word.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi');
    cleanedTitle = cleanedTitle.replace(regex, '');
  });

  return cleanedTitle.replace(/\s+/g, ' ').trim();
}

/**
 * Cleans color-specific product titles (Meriwool, Wool, etc.)
 * Maintains backward compatibility with existing cleanColorTitleJS function
 * @param {string} title - The color product title to clean
 * @returns {string} - The cleaned color title
 */
function cleanColorTitle(title) {
  const colorWordsToRemove = [
    '100%',
    'Meriwool',
    'Wool',
    'Lana',
    'Merino',
    'Superwash',
    'The',
    'Bling',
    'Yarn',
    'Petite',
    'Alpaca',
    'Baby',
    'Pima',
    'Cotton',
    'Wave',
    'Squishy',
    'Twist & Shout',
    'Tweed',
    'Mixed',
    'Touch me Mohair',
    'Merifine',
    'Meripaca',
    'Bamboo',
    'Embroidery',
    'Thread',
    'Fuzzy',
    'Boucle Cloud',
  ];

  let cleanedTitle = cleanProductTitle(title, colorWordsToRemove);

  // Special case for Yarnicorn
  if (cleanedTitle.toLowerCase().includes('icorn')) {
    cleanedTitle = 'Yarnicorn';
  }

  return cleanedTitle;
}

/**
 * Finds a product option by searching through multiple localized names
 * @param {Array} options - Array of product options (product.options_with_values)
 * @param {Array<string>} searchNames - Array of possible option names (lowercase)
 * @returns {Object|null} - The matching option or null
 */
function findOptionByNames(options, searchNames) {
  if (!options || !Array.isArray(options) || !searchNames || !Array.isArray(searchNames)) {
    return null;
  }

  for (const option of options) {
    const optionNameLower = option.name.toLowerCase();

    for (const searchName of searchNames) {
      if (optionNameLower === searchName.trim().toLowerCase()) {
        return option;
      }
    }
  }

  return null;
}

/**
 * Updates data attributes for needle selection in the product form
 * This function reads the selected needle option and updates container attributes
 * with needle product information (name, id, sku, weight, quantity)
 * Uses position-based matching: array index corresponds to variant option index
 */
function updateNeedlesDataAttributes() {
  const needlesContainer = document.querySelector('[data-needles-container]');
  const needlesDataScript = document.querySelector('[data-needles-data]');

  if (!needlesContainer || !needlesDataScript) return;

  let needlesData = [];
  try {
    needlesData = JSON.parse(needlesDataScript.textContent);
  } catch (e) {
    console.error('Error parsing needles data:', e);
    return;
  }

  const checkedNeedleRadio = needlesContainer.querySelector('.variant-radio:checked');

  if (!checkedNeedleRadio) return;

  const isWithoutNeedles = checkedNeedleRadio.closest('.without-needles-option');

  if (isWithoutNeedles) {
    needlesContainer.setAttribute('data-selected-needle-name', '');
    needlesContainer.setAttribute('data-selected-needle-id', '');
    needlesContainer.setAttribute('data-selected-needle-sku', '');
    needlesContainer.setAttribute('data-selected-needle-weight', '');
    needlesContainer.setAttribute('data-selected-needle-quantity', '0');
  } else {
    const radioIndex = parseInt(checkedNeedleRadio.getAttribute('data-needle-index'), 10);
    
    if (isNaN(radioIndex) || radioIndex < 0) {
      console.warn('Invalid needle index');
      needlesContainer.setAttribute('data-selected-needle-name', '');
      needlesContainer.setAttribute('data-selected-needle-id', '');
      needlesContainer.setAttribute('data-selected-needle-sku', '');
      needlesContainer.setAttribute('data-selected-needle-weight', '');
      needlesContainer.setAttribute('data-selected-needle-quantity', '0');
      return;
    }

    const matchedNeedle = needlesData[radioIndex];

    if (matchedNeedle) {
      needlesContainer.setAttribute('data-selected-needle-name', matchedNeedle.title || '');
      needlesContainer.setAttribute('data-selected-needle-id', matchedNeedle.id || '');
      needlesContainer.setAttribute('data-selected-needle-sku', matchedNeedle.variants?.[0]?.sku || '');
      needlesContainer.setAttribute('data-selected-needle-weight', matchedNeedle.variants?.[0]?.weight || '');
      needlesContainer.setAttribute('data-selected-needle-quantity', '1');
    } else {
      console.warn(`No needle found at index ${radioIndex}`);
      needlesContainer.setAttribute('data-selected-needle-name', '');
      needlesContainer.setAttribute('data-selected-needle-id', '');
      needlesContainer.setAttribute('data-selected-needle-sku', '');
      needlesContainer.setAttribute('data-selected-needle-weight', '');
      needlesContainer.setAttribute('data-selected-needle-quantity', '0');
    }
  }
}

// Backward compatibility alias
 var cleanColorTitleJS = cleanColorTitle;